import { App, Plugin } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import type { HomePortalAppState } from "../app";
import {
  ActivateToolModal,
  SubmitActivatePayload,
} from "../components/activate-tool-modal";
import {
  BillingAddressModal,
  SubmitBillingAddressPayload,
} from "../components/billing-address-modal";
import { CancelSubscriptionDialog } from "../components/cancel-subscription-dialog";
import {
  ChangePlanModal,
  SubmitChangePlanPayload,
} from "../components/change-plan-modal";
import {
  CreateAppModal,
  CreateAppModalState,
} from "../components/create-app-modal";
import {
  HomePortalComponentState,
  HomePortalPage,
} from "../components/home-portal-page";
import { formatPeriodEndDate } from "../components/pricing-tab";
import {
  SelectPlanModal,
  SubmitSelectPlanPayload,
} from "../components/select-plan-modal";
import {
  APP_ROUTES,
  DOWNGRADE_BLOCKED_ERROR_CODE,
  UPGRADE_BLOCKED_CANCELLATION_SCHEDULED_ERROR_CODE,
} from "../constants";
import { BACKEND_URL } from "../env";
import { activateApp } from "../helpers/activate-app";
import {
  createApp,
  CreateAppData,
  CreateAppResult,
  type DeleteQueueStatus,
  enqueueAppDeletion,
  fetchAppDeleteQueueStatus,
  fetchApps,
} from "../helpers/fetch-apps";
import {
  fetchBillingAddress,
  saveBillingAddress,
} from "../helpers/fetch-billing-address";
import { fetchClients, updateClientLanguage } from "../helpers/fetch-clients";
import { fetchUserProfile } from "../helpers/fetch-profile";
import {
  cancelSubscription,
  confirmPaymentMethodUpdate,
  createPaymentMethodUpdateSetupIntent,
  fetchInvoices,
  fetchPlans,
  fetchSeatInfo,
  fetchSubscription,
  requestPlanChange,
  selectPlan as selectPlanRequest,
} from "../helpers/fetch-subscription";
import {
  changeUserRole,
  deleteUser,
  fetchUsers,
  inviteUsers,
  transferOwnership,
} from "../helpers/fetch-users";
import { getUserRoleFromProfile } from "../helpers/get-user-role";
import {
  BillingAddress,
  Client,
  Invoice,
  MfaPolicy,
  Plan,
  PlanKey,
  SeatInfo,
  Subscription,
  USER_ROLE,
} from "../types/entities";

export type HomePortalPluginState = HomePortalComponentState &
  CreateAppModalState & {
    clients?: Client[];
    selectedClient?: Client;
    plans?: Plan[];
    subscription?: Subscription | null;
    seatInfo?: SeatInfo | null;
    billingAddress?: BillingAddress | null;
    /** Invoice history (`GET /subscriptions/invoices`, #2791/#2794) —
     * fetched for both the Owner and an Admin (read-only), see
     * `PricingTab`/`InvoiceHistoryList`. */
    invoices?: Invoice[];
    activateModal?: {
      isOpen: boolean;
      appId?: string;
      isSubmitting?: boolean;
      errorMessage?: string;
    };
    selectPlanModal?: {
      isOpen: boolean;
      isSubmitting?: boolean;
      errorMessage?: string;
      /** Step 1 ("plan") is the existing plan + billing picker; step 2
       * ("payment") swaps in `StripeCheckoutForm`, in the same dialog — see
       * #2797 and the UX/UI design spec comment on #2785. */
      checkoutStep?: "plan" | "payment";
      /** Set once `POST /subscriptions` (#2792) returns a Stripe
       * PaymentIntent client secret; cleared whenever the modal is reset. */
      clientSecret?: string;
    };
    changePlanModal?: {
      isOpen: boolean;
      isSubmitting?: boolean;
      errorMessage?: string;
      /** Set when `errorMessage` was raised by the backend's
       * `DOWNGRADE_BLOCKED` guard — see `ChangePlanModalState.downgradeBlocked`
       * on the component for how it changes rendering. */
      downgradeBlocked?: boolean;
      /** Set when `errorMessage` was raised by the backend's
       * `UPGRADE_BLOCKED_CANCELLATION_SCHEDULED` guard — see
       * `ChangePlanModalState.cancellationScheduledBlocked` on the
       * component for how it changes rendering (#2785). */
      cancellationScheduledBlocked?: boolean;
    };
    cancelSubscriptionDialog?: {
      isOpen: boolean;
      isSubmitting?: boolean;
      errorMessage?: string;
    };
    billingAddressModal?: {
      isOpen: boolean;
      isSubmitting?: boolean;
      errorMessage?: string;
    };
    mfaPolicy?: MfaPolicy | undefined;
    deleteQueueProgress?: {
      isQueueing?: boolean;
      isPolling?: boolean;
      status?: DeleteQueueStatus | null;
      errorMessage?: string;
    };
  };

const DELETE_QUEUE_POLL_INTERVAL_MS = 2000;
const MAX_DELETE_QUEUE_POLL_FAILURES = 3;
const TERMINAL_DELETE_QUEUE_STATUSES = new Set(["success", "failed"]);

/**
 * Bounded poll for the subscription to leave `past_due` after the Owner
 * confirms a replacement card (#2795). Deliberately much shorter/simpler
 * than the delete-queue poll above: this is only a short wait for
 * Stripe's own webhook (`invoice.paid`, #2789) to land, not a long-running
 * queue operation. If the webhook hasn't landed within this window, the
 * banner simply keeps showing its "we'll retry shortly" copy — the next
 * page load/navigation re-derives `isVisible` straight from
 * `subscription.status`, which is an acceptable fallback per the #2785 UX
 * spec ("reload-on-next-navigation is also fine").
 */
const PAYMENT_METHOD_RETRY_POLL_INTERVAL_MS = 3000;
const MAX_PAYMENT_METHOD_RETRY_POLL_ATTEMPTS = 5;

function isDeleteQueueTerminal(status: DeleteQueueStatus): boolean {
  return (
    !status.isRunning &&
    status.entries.every((entry) =>
      TERMINAL_DELETE_QUEUE_STATUSES.has(entry.status)
    )
  );
}

function hasDeleteQueueFailures(status: DeleteQueueStatus): boolean {
  return status.entries.some((entry) => entry.status === "failed");
}

function filterAppsWithActiveDeleteEntries(
  apps: Awaited<ReturnType<typeof fetchApps>>,
  status: DeleteQueueStatus | null | undefined,
  clientId?: string
): Awaited<ReturnType<typeof fetchApps>> {
  if (!status) return apps;

  const activeAppIds = new Set(
    status.entries
      .filter((entry) => entry.clientId === clientId)
      .filter((entry) => !TERMINAL_DELETE_QUEUE_STATUSES.has(entry.status))
      .map((entry) => entry.appId)
  );
  if (activeAppIds.size === 0) return apps;

  return apps.filter((app) => !activeAppIds.has(app.id));
}

function getErrorToast(
  result: CreateAppResult,
  language: SupportedLanguage
): { message: string; referenceId?: string } {
  const error = result.error;
  if (error?.status === 409) {
    return {
      message: getLocalizedMessage(
        language,
        "createAppModal.validation.name.alreadyExists"
      ),
    };
  }

  // Server failures can carry internal details; only 4xx text is meant for users.
  if (error?.status && error.status < 500 && error.message) {
    return { message: error.message };
  }

  return error?.referenceId
    ? {
        message: getLocalizedMessage(
          language,
          "homePortalPage.createApp.failedWithReference",
          { referenceId: error.referenceId }
        ),
        referenceId: error.referenceId,
      }
    : {
        message: getLocalizedMessage(
          language,
          "homePortalPage.createApp.failed"
        ),
      };
}

/**
 * Thrown by the `submit-change-plan` handler when the backend's
 * `DOWNGRADE_BLOCKED` guard rejects the request, so the `catch` block below
 * can tell it apart from any other failure (network error, validation,
 * etc.) and set `changePlanModal.downgradeBlocked` — branching on the
 * backend's machine-readable `errorCode`, never on `message` text (#2796).
 */
class DowngradeBlockedError extends Error {}

/**
 * Thrown by the `submit-change-plan` handler when the backend's
 * `UPGRADE_BLOCKED_CANCELLATION_SCHEDULED` guard rejects the request —
 * i.e. the subscription is already `cancel_scheduled`, so the "Change
 * Plan" button should normally be disabled already (see `PricingTab`).
 * This only fires via a stale page that hasn't picked up that disabled
 * state yet, so the `catch` block below sets
 * `changePlanModal.cancellationScheduledBlocked` — branching on the
 * backend's machine-readable `errorCode`, never on `message` text (#2785).
 */
class UpgradeBlockedCancellationScheduledError extends Error {}

function localizeApiError(
  language: SupportedLanguage,
  error: { status?: number; code?: string; message?: string } | undefined,
  fallbackKey: string
): string {
  if (!error) return getLocalizedMessage(language, fallbackKey);
  // Map well-known codes to specific i18n keys
  if (error.code === "not_found") {
    return getLocalizedMessage(language, "homePortalPage.userNotFound");
  }

  // For conflict on creating app, handled elsewhere; default to message if available
  if (error.message) return error.message;

  return getLocalizedMessage(language, fallbackKey);
}

export class HomePortalPlugin implements Plugin<HomePortalAppState> {
  private app?: App<HomePortalAppState>;
  private deleteQueuePollingTimer?: ReturnType<typeof setTimeout>;
  private isDeleteQueuePolling = false;
  private deleteQueueGeneration = 0;
  private outstandingDeleteEnqueues = 0;
  private hasActiveDeleteQueue = false;
  private deleteQueuePollFailures = 0;
  private deleteQueueBaseline?: { queueId: string; failureCount: number };
  private deleteQueueHistoryLost = false;
  private paymentMethodRetryPollTimer?: ReturnType<typeof setTimeout>;
  private paymentMethodRetryPollGeneration = 0;

  private stopPaymentMethodRetryPolling(): void {
    if (this.paymentMethodRetryPollTimer) {
      clearTimeout(this.paymentMethodRetryPollTimer);
      this.paymentMethodRetryPollTimer = undefined;
    }
    this.paymentMethodRetryPollGeneration += 1;
  }

  private startPaymentMethodRetryPolling(clientId: string): void {
    if (!this.app) return;
    this.stopPaymentMethodRetryPolling();
    const generation = this.paymentMethodRetryPollGeneration;
    void this.pollPaymentMethodRetry(clientId, generation, 0);
  }

  private async pollPaymentMethodRetry(
    clientId: string,
    generation: number,
    attempt: number
  ): Promise<void> {
    if (!this.app || generation !== this.paymentMethodRetryPollGeneration) {
      return;
    }
    const subscription = await fetchSubscription(clientId).catch(() => null);
    if (generation !== this.paymentMethodRetryPollGeneration) return;

    if (subscription && subscription.status !== "past_due") {
      this.app.patchAppState({
        subscription,
        isPaymentMethodRetryPending: false,
      });
      return;
    }

    if (attempt + 1 >= MAX_PAYMENT_METHOD_RETRY_POLL_ATTEMPTS) {
      // Stop polling but leave `isPaymentMethodRetryPending` as-is — see
      // the module doc comment on this constant for the fallback.
      return;
    }
    this.paymentMethodRetryPollTimer = setTimeout(() => {
      this.paymentMethodRetryPollTimer = undefined;
      void this.pollPaymentMethodRetry(clientId, generation, attempt + 1);
    }, PAYMENT_METHOD_RETRY_POLL_INTERVAL_MS);
  }

  private startDeleteQueuePolling(clientId?: string): void {
    if (!this.app || this.isDeleteQueuePolling) return;

    this.isDeleteQueuePolling = true;
    this.deleteQueuePollFailures = 0;
    void this.pollDeleteQueue(clientId, this.deleteQueueGeneration);
  }

  private scheduleDeleteQueuePolling(
    clientId: string | undefined,
    generation: number
  ): void {
    this.deleteQueuePollingTimer = setTimeout(() => {
      this.deleteQueuePollingTimer = undefined;
      void this.pollDeleteQueue(clientId, generation);
    }, DELETE_QUEUE_POLL_INTERVAL_MS);
  }

  private stopDeleteQueuePolling(): void {
    if (this.deleteQueuePollingTimer) {
      clearTimeout(this.deleteQueuePollingTimer);
      this.deleteQueuePollingTimer = undefined;
    }
    this.isDeleteQueuePolling = false;
  }

  private async pollDeleteQueue(
    clientId: string | undefined,
    generation: number
  ): Promise<void> {
    if (
      generation !== this.deleteQueueGeneration ||
      this.outstandingDeleteEnqueues > 0
    )
      return;
    if (!this.app) {
      this.stopDeleteQueuePolling();
      return;
    }

    const language = this.app.getAppState().language || "de";
    let isCompleting = false;

    try {
      const status = await fetchAppDeleteQueueStatus(clientId);
      if (generation !== this.deleteQueueGeneration) return;
      if (!this.app) {
        this.stopDeleteQueuePolling();
        return;
      }

      if (!status) {
        throw new Error("Delete queue status unavailable");
      }
      this.deleteQueuePollFailures = 0;

      this.app.patchAppState({
        deleteQueueProgress: {
          isQueueing: false,
          isPolling: true,
          status,
        },
      });

      if (!isDeleteQueueTerminal(status)) {
        this.scheduleDeleteQueuePolling(clientId, generation);
        return;
      }

      isCompleting = true;
      const currentState = this.app.getAppState();
      const currentClientId =
        currentState.selectedClient?.id ?? currentState.clients?.[0]?.id;
      let apps: HomePortalAppState["apps"] | undefined;
      if (currentClientId) {
        try {
          const refreshedApps = await fetchApps(currentClientId);
          if (!this.app || generation !== this.deleteQueueGeneration) return;

          const latestState = this.app.getAppState();
          const latestClientId =
            latestState.selectedClient?.id ?? latestState.clients?.[0]?.id;
          if (latestClientId === currentClientId) {
            apps = filterAppsWithActiveDeleteEntries(
              refreshedApps,
              status,
              currentClientId
            );
          }
        } catch {
          // Queue completion is still authoritative even if the refresh failed.
        }
      }
      if (!this.app || generation !== this.deleteQueueGeneration) return;

      this.stopDeleteQueuePolling();
      this.hasActiveDeleteQueue = false;
      const baseline = this.deleteQueueBaseline;
      const hasFailures =
        hasDeleteQueueFailures(status) ||
        this.deleteQueueHistoryLost ||
        (baseline !== undefined &&
          (status.queueId !== baseline.queueId ||
            status.failureCount === undefined ||
            status.failureCount > baseline.failureCount));
      const failedEntry = status.entries.find(
        (entry) => entry.status === "failed" && entry.lastError
      );
      const failureMessage = localizeApiError(
        language,
        failedEntry ? { message: failedEntry.lastError } : undefined,
        "homePortalPage.failedToDeleteReport"
      );
      this.app.patchAppState({
        ...(apps ? { apps } : {}),
        deleteQueueProgress: {
          isQueueing: false,
          isPolling: false,
          status,
        },
        snackbar: {
          message: hasFailures
            ? failureMessage
            : getLocalizedMessage(
                language,
                "homePortalPage.deleteQueueCompleted"
              ),
          severity: hasFailures ? "warning" : "success",
          open: true,
          type: "snackbar",
        },
      });
    } catch {
      if (generation !== this.deleteQueueGeneration) return;
      if (!this.app) {
        this.stopDeleteQueuePolling();
        return;
      }

      this.deleteQueuePollFailures += 1;
      const stopped =
        isCompleting ||
        this.deleteQueuePollFailures >= MAX_DELETE_QUEUE_POLL_FAILURES;
      const message = getLocalizedMessage(
        language,
        "homePortalPage.failedToDeleteReport"
      );
      if (stopped) {
        this.stopDeleteQueuePolling();
        this.hasActiveDeleteQueue = false;
      }
      let apps: HomePortalAppState["apps"] | undefined;
      if (stopped) {
        const currentState = this.app.getAppState();
        const currentClientId =
          currentState.selectedClient?.id ?? currentState.clients?.[0]?.id;
        if (currentClientId) {
          try {
            const refreshedApps = await fetchApps(currentClientId);
            if (!this.app || generation !== this.deleteQueueGeneration) return;
            const latestState = this.app.getAppState();
            const latestClientId =
              latestState.selectedClient?.id ?? latestState.clients?.[0]?.id;
            if (latestClientId === currentClientId) {
              apps = refreshedApps;
            }
          } catch {
            // Keep current rows when fallback refresh fails.
          }
        }
      }
      this.app.patchAppState({
        ...(apps ? { apps } : {}),
        deleteQueueProgress: {
          ...(this.app.getAppState().deleteQueueProgress ?? {}),
          isQueueing: false,
          isPolling: !stopped,
          errorMessage: message,
        },
        ...(stopped
          ? {
              snackbar: {
                message,
                severity: "error" as const,
                open: true,
                type: "snackbar" as const,
              },
            }
          : {}),
      });
      if (!stopped) this.scheduleDeleteQueuePolling(clientId, generation);
    }
  }

  onRegister(app: App<HomePortalAppState>): void {
    this.app = app;

    const view = app.createView({
      name: "HomePortal",
    });

    const homePortalComponent = view.addComponent({
      component: HomePortalPage,
    });

    homePortalComponent.mapState((state) => {
      const userProfile = state?.userProfile;
      const deleteQueueEntries =
        state.deleteQueueProgress?.status?.entries ?? [];
      const activeDeleteQueueEntry = deleteQueueEntries.find(
        (entry) => entry.status === "running"
      );
      return {
        apps: state.apps ?? [],
        client: state.selectedClient ?? state.clients?.[0],
        clients: state.clients ?? [],
        users: state.users ?? [],
        userProfile,
        showRoleChangeConfirmDialog: state.showRoleChangeConfirmDialog ?? false,
        pendingRoleChangeUserId: state.pendingRoleChangeUserId,
        showTransferOwnershipConfirmDialog:
          state.showTransferOwnershipConfirmDialog ?? false,
        pendingTransferOwnershipUserId: state.pendingTransferOwnershipUserId,
        isSwitchingClient: state.isSwitchingClient ?? false,
        plans: state.plans ?? [],
        subscription: state.subscription ?? null,
        seatInfo: state.seatInfo ?? null,
        billingAddress: state.billingAddress ?? null,
        invoices: state.invoices ?? [],
        deleteQueueDialog: {
          isOpen:
            (state.deleteQueueProgress?.isQueueing ?? false) ||
            (state.deleteQueueProgress?.isPolling ?? false),
          processed: deleteQueueEntries.filter(
            (entry) => entry.status === "success" || entry.status === "failed"
          ).length,
          total: deleteQueueEntries.length,
          failed: deleteQueueEntries.filter(
            (entry) => entry.status === "failed"
          ).length,
          activeAppName: activeDeleteQueueEntry?.appName,
        },
        language: state.language,
        mfaPolicy: state.mfaPolicy,
        isPaymentMethodRetryPending: state.isPaymentMethodRetryPending ?? false,
        updatePaymentMethodDialog: {
          isOpen: state.updatePaymentMethodDialog?.isOpen ?? false,
          isSubmitting: state.updatePaymentMethodDialog?.isSubmitting ?? false,
          errorMessage: state.updatePaymentMethodDialog?.errorMessage,
          clientSecret: state.updatePaymentMethodDialog?.clientSecret,
        },
      };
    });

    const createAppModalComponent = view.addComponent({
      component: CreateAppModal,
    });

    createAppModalComponent.mapState((state) => ({
      isOpen: state.isOpen ?? false,
      isCreatingApp: state.isCreatingApp ?? false,
      successMessage: state.successMessage,
      selectedClientId:
        state?.selectedClient?.id ?? state?.clients?.[0]?.id ?? undefined,
      language: state.language,
    }));

    const activateToolModalComponent = view.addComponent({
      component: ActivateToolModal,
    });

    activateToolModalComponent.mapState((state) => {
      const appId = state.activateModal?.appId;
      const app = state.apps?.find((a) => a.id === appId);
      const sub = state.subscription ?? null;
      const plan = sub
        ? state.plans?.find((p) => p.key === sub.planKey)
        : undefined;
      const includedUsed = (state.apps ?? []).filter(
        (a) => a.activationStatus === "active_included"
      ).length;
      const includedSlotAvailable = plan
        ? includedUsed < plan.includedTools
        : false;
      return {
        isOpen: state.activateModal?.isOpen ?? false,
        isSubmitting: state.activateModal?.isSubmitting ?? false,
        errorMessage: state.activateModal?.errorMessage,
        app: app ?? null,
        currentPlan: plan ?? null,
        isPaid: !includedSlotAvailable,
        includedToolsUsed: includedUsed,
        billingAddress: state.billingAddress ?? null,
        language: state.language,
      };
    });

    const selectPlanModalComponent = view.addComponent({
      component: SelectPlanModal,
    });

    selectPlanModalComponent.mapState((state) => ({
      isOpen: state.selectPlanModal?.isOpen ?? false,
      isSubmitting: state.selectPlanModal?.isSubmitting ?? false,
      errorMessage: state.selectPlanModal?.errorMessage,
      checkoutStep: state.selectPlanModal?.checkoutStep ?? "plan",
      clientSecret: state.selectPlanModal?.clientSecret,
      plans: state.plans ?? [],
      billingAddress: state.billingAddress ?? null,
      language: state.language,
    }));

    const changePlanModalComponent = view.addComponent({
      component: ChangePlanModal,
    });

    changePlanModalComponent.mapState((state) => {
      const sub = state.subscription ?? null;
      const currentPlan = sub
        ? state.plans?.find((p) => p.key === sub.planKey)
        : undefined;
      return {
        isOpen: state.changePlanModal?.isOpen ?? false,
        isSubmitting: state.changePlanModal?.isSubmitting ?? false,
        errorMessage: state.changePlanModal?.errorMessage,
        downgradeBlocked: state.changePlanModal?.downgradeBlocked ?? false,
        cancellationScheduledBlocked:
          state.changePlanModal?.cancellationScheduledBlocked ?? false,
        plans: state.plans ?? [],
        currentPlan: currentPlan ?? null,
        billingAddress: state.billingAddress ?? null,
        language: state.language,
        // Needed by the modal's own proration-preview fetch
        // (`GET /subscriptions/upgrade-preview`, #2787/#2798) — see
        // `change-plan-modal.tsx`.
        clientId: state.selectedClient?.id ?? state.clients?.[0]?.id,
      };
    });

    const cancelSubscriptionDialogComponent = view.addComponent({
      component: CancelSubscriptionDialog,
    });

    cancelSubscriptionDialogComponent.mapState((state) => {
      const sub = state.subscription ?? null;
      const currentPlan = sub
        ? state.plans?.find((p) => p.key === sub.planKey)
        : undefined;
      return {
        isOpen: state.cancelSubscriptionDialog?.isOpen ?? false,
        isSubmitting: state.cancelSubscriptionDialog?.isSubmitting ?? false,
        errorMessage: state.cancelSubscriptionDialog?.errorMessage,
        currentPlan: currentPlan ?? null,
        periodEndDate: sub?.cancelAt
          ? formatPeriodEndDate(sub.cancelAt, state.language)
          : null,
        language: state.language,
      };
    });

    const billingAddressModalComponent = view.addComponent({
      component: BillingAddressModal,
    });

    billingAddressModalComponent.mapState((state) => ({
      isOpen: state.billingAddressModal?.isOpen ?? false,
      isSubmitting: state.billingAddressModal?.isSubmitting ?? false,
      errorMessage: state.billingAddressModal?.errorMessage,
      billingAddress: state.billingAddress ?? null,
      language: state.language,
    }));

    homePortalComponent.on("activate-tool", async ({ payload }) => {
      if (!this.app) return;
      const { appId } = (payload ?? {}) as { appId?: string };
      this.app.patchAppState({
        activateModal: { isOpen: true, appId },
      });
    });

    homePortalComponent.on("open-select-plan-modal", () => {
      if (!this.app) return;
      this.app.patchAppState({
        selectPlanModal: { isOpen: true },
      });
    });

    homePortalComponent.on("open-change-plan-modal", () => {
      if (!this.app) return;
      this.app.patchAppState({
        changePlanModal: { isOpen: true },
      });
    });

    homePortalComponent.on("edit-billing-address", () => {
      if (!this.app) return;
      this.app.patchAppState({
        billingAddressModal: { isOpen: true },
      });
    });

    homePortalComponent.on("open-cancel-subscription-dialog", () => {
      if (!this.app) return;
      this.app.patchAppState({
        cancelSubscriptionDialog: { isOpen: true },
      });
    });

    homePortalComponent.on("change-language", ({ payload }) => {
      if (!this.app || !payload) return;
      const { language } = payload as { language: SupportedLanguage };
      this.app.patchAppState({
        language,
      });
      const clientId = this.app.getAppState().selectedClient?.id;

      if (!clientId) return;

      updateClientLanguage(clientId, language);
    });

    billingAddressModalComponent.on("close-billing-address-modal", () => {
      if (!this.app) return;
      this.app.patchAppState({
        billingAddressModal: { isOpen: false },
      });
    });

    billingAddressModalComponent.on(
      "submit-billing-address",
      async ({ payload }) => {
        if (!this.app) return;
        const data = (payload ?? {}) as SubmitBillingAddressPayload;
        const state = this.app.getAppState();
        const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
        if (!clientId || !data.billingAddress) return;
        this.app.patchAppState({
          billingAddressModal: { isOpen: true, isSubmitting: true },
        });
        try {
          const result = await saveBillingAddress(
            clientId,
            data.billingAddress
          );
          if (!result.success) {
            throw new Error(
              localizeApiError(
                state?.language || "de",
                result.error,
                "homePortalPage.billingAddress.saveFailed"
              )
            );
          }
          this.app.patchAppState({
            billingAddress: result.address ?? null,
            billingAddressModal: { isOpen: false },
          });
        } catch (err) {
          this.app.patchAppState({
            billingAddressModal: {
              isOpen: true,
              isSubmitting: false,
              errorMessage:
                err instanceof Error
                  ? err.message
                  : getLocalizedMessage(
                      state?.language || "de",
                      "homePortalPage.billingAddress.saveFailed"
                    ),
            },
          });
        }
      }
    );

    activateToolModalComponent.on("close-activate-modal", () => {
      if (!this.app) return;
      this.app.patchAppState({
        activateModal: { isOpen: false },
      });
    });

    activateToolModalComponent.on("submit-activate", async ({ payload }) => {
      if (!this.app) return;
      const data = (payload ?? {}) as SubmitActivatePayload;
      const state = this.app.getAppState();
      const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
      if (!clientId || !data.appId) return;
      this.app.patchAppState({
        activateModal: {
          isOpen: true,
          appId: data.appId,
          isSubmitting: true,
        },
      });
      try {
        if (data.billingAddress) {
          await saveBillingAddress(clientId, data.billingAddress);
        }
        const activateResult = await activateApp(data.appId);
        if (!activateResult.success) {
          throw new Error(
            localizeApiError(
              state?.language || "de",
              activateResult.error,
              "homePortalPage.activateTool.failed"
            )
          );
        }
        const [apps, billingAddress] = await Promise.all([
          fetchApps(clientId),
          fetchBillingAddress(clientId).catch(() => null),
        ]);
        this.app.patchAppState({
          apps,
          billingAddress,
          activateModal: { isOpen: false },
        });
      } catch (err) {
        this.app.patchAppState({
          activateModal: {
            isOpen: true,
            appId: data.appId,
            isSubmitting: false,
            errorMessage:
              err instanceof Error
                ? err.message
                : getLocalizedMessage(
                    state?.language || "de",
                    "homePortalPage.activateTool.failed"
                  ),
          },
        });
      }
    });

    selectPlanModalComponent.on("close-select-plan-modal", () => {
      if (!this.app) return;
      // A full reset (not a merge) — this is also what makes an abandoned
      // checkout "safe" to reopen (#2797, UX spec "Abandoned checkout"):
      // `checkoutStep`/`clientSecret` are dropped along with everything
      // else, so the dialog always starts fresh at the plan step next
      // time it opens, never stuck mid-payment.
      this.app.patchAppState({
        selectPlanModal: { isOpen: false },
      });
    });

    // Fired by `StripeCheckoutForm` (via `SelectPlanModal`) instead of
    // `close-select-plan-modal` while the dialog is on the payment step,
    // per the UX spec's "Abandoned checkout" flow — same full reset,
    // named differently so it is observable which step was abandoned.
    selectPlanModalComponent.on("cancel-payment", () => {
      if (!this.app) return;
      this.app.patchAppState({
        selectPlanModal: { isOpen: false },
      });
    });

    selectPlanModalComponent.on("submit-select-plan", async ({ payload }) => {
      if (!this.app) return;
      const data = (payload ?? {}) as SubmitSelectPlanPayload;
      const state = this.app.getAppState();
      const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
      if (!clientId || !data.planKey) return;
      this.app.patchAppState({
        selectPlanModal: { isOpen: true, isSubmitting: true },
      });
      try {
        if (data.billingAddress) {
          await saveBillingAddress(clientId, data.billingAddress);
        }
        const selectResult = await selectPlanRequest(
          clientId,
          data.planKey as PlanKey
        );
        if (!selectResult.success || !selectResult.clientSecret) {
          throw new Error(
            localizeApiError(
              state?.language || "de",
              selectResult.error,
              "homePortalPage.selectPlan.failed"
            )
          );
        }
        // Step 1 → Step 2, in place (no modal close/reopen, per the UX
        // spec): the local `Subscription` now exists in `incomplete`
        // status, and the Owner still needs to confirm payment with
        // Stripe Elements before anything is `active` — see
        // `StripeCheckoutForm`/`payment-succeeded` below for the rest of
        // the flow.
        this.app.patchAppState({
          selectPlanModal: {
            isOpen: true,
            isSubmitting: false,
            checkoutStep: "payment",
            clientSecret: selectResult.clientSecret,
          },
        });
      } catch (err) {
        this.app.patchAppState({
          selectPlanModal: {
            isOpen: true,
            isSubmitting: false,
            errorMessage:
              err instanceof Error
                ? err.message
                : getLocalizedMessage(
                    state?.language || "de",
                    "homePortalPage.selectPlan.failed"
                  ),
          },
        });
      }
    });

    // The Stripe confirmPayment call itself happens inside
    // `StripeCheckoutForm` (it needs the `stripe`/`elements` instances from
    // `useStripe`/`useElements`, which only exist inside its own
    // `<Elements>` provider — this plugin has no access to them). These
    // three handlers only react to the outcome it reports.
    selectPlanModalComponent.on("submit-payment", () => {
      if (!this.app) return;
      const current = this.app.getAppState().selectPlanModal;
      this.app.patchAppState({
        selectPlanModal: {
          ...current,
          isOpen: true,
          isSubmitting: true,
          errorMessage: undefined,
        },
      });
    });

    selectPlanModalComponent.on("payment-failed", ({ payload }) => {
      if (!this.app) return;
      const { message } = (payload ?? {}) as { message?: string };
      const state = this.app.getAppState();
      const current = state.selectPlanModal;
      this.app.patchAppState({
        selectPlanModal: {
          ...current,
          isOpen: true,
          isSubmitting: false,
          errorMessage:
            message ||
            getLocalizedMessage(
              state.language || "de",
              "homePortalPage.selectPlan.payment.genericError"
            ),
        },
      });
    });

    selectPlanModalComponent.on("payment-succeeded", async () => {
      if (!this.app) return;
      const state = this.app.getAppState();
      const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
      // Close first: Stripe has confirmed the payment on its side, so
      // there is nothing left for the Owner to do here. The webhook
      // handler (#2792/StripeWebhookService) is what actually flips the
      // local subscription to `active` — it may not have run yet, so
      // whatever `fetchSubscription` returns next (possibly still
      // `incomplete`) is what `PricingTab` will show until it does.
      this.app.patchAppState({
        selectPlanModal: { isOpen: false },
      });
      if (!clientId) return;
      const [subscription, seatInfo, billingAddress, invoices] =
        await Promise.all([
          fetchSubscription(clientId).catch(() => null),
          fetchSeatInfo(clientId).catch(() => null),
          fetchBillingAddress(clientId).catch(() => null),
          fetchInvoices(clientId).catch(() => []),
        ]);
      this.app.patchAppState({
        subscription,
        seatInfo,
        billingAddress,
        invoices,
      });
    });

    changePlanModalComponent.on("close-change-plan-modal", () => {
      if (!this.app) return;
      this.app.patchAppState({
        changePlanModal: { isOpen: false },
      });
    });

    changePlanModalComponent.on("submit-change-plan", async ({ payload }) => {
      if (!this.app) return;
      const data = (payload ?? {}) as SubmitChangePlanPayload;
      const state = this.app.getAppState();
      const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
      if (!clientId || !data.planKey) return;
      this.app.patchAppState({
        changePlanModal: { isOpen: true, isSubmitting: true },
      });
      try {
        if (data.billingAddress) {
          await saveBillingAddress(clientId, data.billingAddress);
        }
        // Only call the upgrade-only plan endpoint when the plan actually
        // changed; a billing-only submit just saves the address above.
        const planChanged = data.planKey !== state.subscription?.planKey;
        if (planChanged) {
          const changeResult = await requestPlanChange(
            clientId,
            data.planKey as PlanKey
          );
          if (!changeResult.success) {
            const message = localizeApiError(
              state?.language || "de",
              changeResult.error,
              "homePortalPage.changePlan.failed"
            );
            if (changeResult.error?.code === DOWNGRADE_BLOCKED_ERROR_CODE) {
              throw new DowngradeBlockedError(message);
            }
            if (
              changeResult.error?.code ===
              UPGRADE_BLOCKED_CANCELLATION_SCHEDULED_ERROR_CODE
            ) {
              throw new UpgradeBlockedCancellationScheduledError(message);
            }
            throw new Error(message);
          }
        }
        const [subscription, seatInfo, billingAddress, invoices] =
          await Promise.all([
            fetchSubscription(clientId).catch(() => null),
            fetchSeatInfo(clientId).catch(() => null),
            fetchBillingAddress(clientId).catch(() => null),
            fetchInvoices(clientId).catch(() => []),
          ]);
        this.app.patchAppState({
          subscription,
          seatInfo,
          billingAddress,
          invoices,
          changePlanModal: { isOpen: false },
        });
      } catch (err) {
        this.app.patchAppState({
          changePlanModal: {
            isOpen: true,
            isSubmitting: false,
            downgradeBlocked: err instanceof DowngradeBlockedError,
            cancellationScheduledBlocked:
              err instanceof UpgradeBlockedCancellationScheduledError,
            errorMessage:
              err instanceof Error
                ? err.message
                : getLocalizedMessage(
                    state?.language || "de",
                    "homePortalPage.changePlan.failed"
                  ),
          },
        });
      }
    });

    cancelSubscriptionDialogComponent.on("close-cancel-dialog", () => {
      if (!this.app) return;
      this.app.patchAppState({
        cancelSubscriptionDialog: { isOpen: false },
      });
    });

    cancelSubscriptionDialogComponent.on(
      "confirm-cancel-subscription",
      async () => {
        if (!this.app) return;
        const state = this.app.getAppState();
        const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
        if (!clientId) return;
        this.app.patchAppState({
          cancelSubscriptionDialog: { isOpen: true, isSubmitting: true },
        });
        const result = await cancelSubscription(clientId);
        if (!result.success) {
          this.app.patchAppState({
            cancelSubscriptionDialog: {
              isOpen: true,
              isSubmitting: false,
              errorMessage: localizeApiError(
                state?.language || "de",
                result.error,
                "homePortalPage.cancelSubscription.failed"
              ),
            },
          });
          return;
        }
        // Re-fetch fresh state after the async cancellation call rather
        // than trusting anything read before it — same convention as
        // `submit-change-plan`/`payment-succeeded` above.
        const [subscription, seatInfo, invoices] = await Promise.all([
          fetchSubscription(clientId).catch(() => null),
          fetchSeatInfo(clientId).catch(() => null),
          fetchInvoices(clientId).catch(() => []),
        ]);
        this.app.patchAppState({
          subscription,
          seatInfo,
          invoices,
          cancelSubscriptionDialog: { isOpen: false },
        });
      }
    );

    // --- Update payment method (#2795), opened from `PastDuePaymentBanner`'s
    // inline CTA. `homePortalComponent` receives these (not a separately
    // registered component) because `PastDuePaymentBanner` and
    // `UpdatePaymentMethodDialog` are mounted directly inside
    // `HomePortalPage`, same as its other inline dialogs.

    homePortalComponent.on("open-update-payment-method-dialog", async () => {
      if (!this.app) return;
      const state = this.app.getAppState();
      const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
      if (!clientId) return;

      this.app.patchAppState({
        updatePaymentMethodDialog: { isOpen: true, isSubmitting: true },
      });

      const result = await createPaymentMethodUpdateSetupIntent(clientId);
      if (!result.success || !result.clientSecret) {
        this.app.patchAppState({
          updatePaymentMethodDialog: {
            isOpen: true,
            isSubmitting: false,
            errorMessage: localizeApiError(
              state?.language || "de",
              result.error,
              "updatePaymentMethodDialog.genericError"
            ),
          },
        });
        return;
      }
      this.app.patchAppState({
        updatePaymentMethodDialog: {
          isOpen: true,
          isSubmitting: false,
          clientSecret: result.clientSecret,
        },
      });
    });

    homePortalComponent.on("close-update-payment-method-dialog", () => {
      if (!this.app) return;
      this.app.patchAppState({
        updatePaymentMethodDialog: { isOpen: false },
      });
    });

    homePortalComponent.on("submit-payment-method-update", () => {
      if (!this.app) return;
      const current = this.app.getAppState().updatePaymentMethodDialog;
      this.app.patchAppState({
        updatePaymentMethodDialog: {
          ...current,
          isOpen: true,
          isSubmitting: true,
          errorMessage: undefined,
        },
      });
    });

    homePortalComponent.on("payment-method-update-failed", ({ payload }) => {
      if (!this.app) return;
      const { message } = (payload ?? {}) as { message?: string };
      const current = this.app.getAppState().updatePaymentMethodDialog;
      this.app.patchAppState({
        updatePaymentMethodDialog: {
          ...current,
          isOpen: true,
          isSubmitting: false,
          errorMessage: message,
        },
      });
    });

    homePortalComponent.on(
      "payment-method-update-succeeded",
      async ({ payload }) => {
        if (!this.app) return;
        const state = this.app.getAppState();
        const clientId = state.selectedClient?.id ?? state.clients?.[0]?.id;
        const { paymentMethodId } = (payload ?? {}) as {
          paymentMethodId?: string;
        };

        // Close the dialog and switch the banner to its "we'll retry
        // shortly" substate right away — `UpdatePaymentMethodFormInner`
        // already showed its own success message for 1.5s before firing
        // this event, so there is no need to keep the dialog open too.
        this.app.patchAppState({
          updatePaymentMethodDialog: { isOpen: false },
          isPaymentMethodRetryPending: true,
        });

        if (!clientId || !paymentMethodId) return;

        // Sets the new card as the Stripe customer's default payment
        // method; does NOT force an immediate retry of the failed invoice
        // (see this function's own doc comment in `fetch-subscription.ts`
        // for why — Stripe's own dunning retry picks it up).
        const result = await confirmPaymentMethodUpdate(
          clientId,
          paymentMethodId
        );
        if (result.success && result.subscription) {
          this.app.patchAppState({ subscription: result.subscription });
        }
        // Bounded poll for the webhook-driven `active` transition — see
        // the constants' doc comment above.
        this.startPaymentMethodRetryPolling(clientId);
      }
    );

    homePortalComponent.on("switch-client", async ({ payload }) => {
      if (!payload || !this.app) return;

      const { clientId } = payload as { clientId: string };
      const state = this.app.getAppState();
      const clients = state?.clients ?? [];
      const newClient = clients.find((c) => c.id === clientId);

      if (!newClient) return;

      // Set loading state
      this.app.patchAppState({
        isSwitchingClient: true,
      });

      // Re-derive the user's role for the new client
      const userProfile = state?.userProfile;
      const updatedProfile = userProfile
        ? {
            ...userProfile,
            role: getUserRoleFromProfile(userProfile, newClient?.name),
          }
        : undefined;

      // Fetch apps, users, subscription, seat info, billing and invoice
      // history for the new client
      const [
        apps,
        usersResult,
        subscription,
        seatInfo,
        billingAddress,
        invoices,
        deleteQueueStatus,
      ] = await Promise.all([
        fetchApps(clientId),
        fetchUsers(clientId),
        fetchSubscription(clientId).catch(() => null),
        fetchSeatInfo(clientId).catch(() => null),
        fetchBillingAddress(clientId).catch(() => null),
        fetchInvoices(clientId).catch(() => []),
        fetchAppDeleteQueueStatus(clientId),
      ]);
      const shouldResumeDeleteQueue =
        deleteQueueStatus && !isDeleteQueueTerminal(deleteQueueStatus);
      const visibleApps = filterAppsWithActiveDeleteEntries(
        apps,
        deleteQueueStatus,
        clientId
      );
      const { users, mfaPolicy } = usersResult;

      this.app.patchAppState({
        selectedClient: newClient,
        apps: visibleApps,
        users,
        subscription,
        seatInfo,
        billingAddress,
        invoices,
        userProfile: updatedProfile,
        deleteQueueProgress: shouldResumeDeleteQueue
          ? {
              isQueueing: false,
              isPolling: true,
              status: deleteQueueStatus,
              errorMessage: undefined,
            }
          : {
              isQueueing: false,
              isPolling: false,
              status: null,
              errorMessage: undefined,
            },
        isSwitchingClient: false,
        mfaPolicy,
      });
      this.hasActiveDeleteQueue = Boolean(shouldResumeDeleteQueue);
      if (shouldResumeDeleteQueue) {
        this.startDeleteQueuePolling(clientId);
      } else {
        this.stopDeleteQueuePolling();
      }
      // A different client's past-due state (if any) is no longer relevant
      // to any in-flight poll from the previously selected client.
      this.stopPaymentMethodRetryPolling();
    });

    homePortalComponent.on("create-report", () => {
      this.app?.patchAppState({
        isOpen: true,
      });
    });

    homePortalComponent.on("open-report", ({ payload }) => {
      if (!payload) return;

      console.log("Open report:", (payload as { reportId: string }).reportId);
    });

    homePortalComponent.on("delete-report", async ({ payload }) => {
      if (!payload) return;

      const { reportId } = payload as { reportId: string };

      const state = this.app?.getAppState();
      const selectedClientId =
        state?.selectedClient?.id ?? state?.clients?.[0]?.id;
      const previousApps = state?.apps ?? [];
      const deletedApp = previousApps.find((app) => app.id === reportId);
      const previousAppsOrder = previousApps.map((app) => app.id);

      if (!this.hasActiveDeleteQueue && this.outstandingDeleteEnqueues === 0) {
        this.deleteQueueBaseline = undefined;
        this.deleteQueueHistoryLost = false;
      }
      // Invalidate in-flight status/refresh work before any await. Pause polling
      // until every concurrent enqueue settles so an old terminal snapshot
      // cannot complete a run which still has requests on the way to the server.
      this.deleteQueueGeneration += 1;
      this.outstandingDeleteEnqueues += 1;
      this.stopDeleteQueuePolling();
      this.app?.patchAppState({
        apps: previousApps.filter((app) => app.id !== reportId),
        snackbar: {
          message: getLocalizedMessage(
            state?.language || "de",
            "homePortalPage.deletingReport"
          ),
          severity: "info",
          open: true,
          type: "snackbar",
        },
        deleteQueueProgress: {
          ...(state?.deleteQueueProgress ?? {}),
          ...(!this.hasActiveDeleteQueue ? { status: null } : {}),
          isQueueing: true,
          isPolling: this.hasActiveDeleteQueue,
          errorMessage: undefined,
        },
      });

      const rollbackDeletion = (
        error: Parameters<typeof localizeApiError>[1]
      ) => {
        const message = localizeApiError(
          state?.language || "de",
          error,
          "homePortalPage.failedToDeleteReport"
        );
        const currentState = this.app?.getAppState();
        const currentClientId =
          currentState?.selectedClient?.id ?? currentState?.clients?.[0]?.id;
        const currentApps = currentState?.apps ?? [];
        const wasRestoredAlready = currentApps.some(
          (app) => app.id === reportId
        );
        const restoredApps =
          deletedApp && !wasRestoredAlready
            ? [...currentApps, deletedApp]
            : currentApps;
        const previousAppIndexes = new Map(
          previousAppsOrder.map((id, index) => [id, index])
        );
        const currentAppIndexes = new Map(
          restoredApps.map((app, index) => [app.id, index])
        );
        const sortedRestoredApps = [...restoredApps].sort((first, second) => {
          const firstIndex = previousAppIndexes.get(first.id);
          const secondIndex = previousAppIndexes.get(second.id);
          if (firstIndex === undefined) {
            return secondIndex === undefined
              ? (currentAppIndexes.get(first.id) ?? Number.MAX_SAFE_INTEGER) -
                  (currentAppIndexes.get(second.id) ?? Number.MAX_SAFE_INTEGER)
              : 1;
          }
          if (secondIndex === undefined) return -1;
          return firstIndex - secondIndex;
        });
        this.app?.patchAppState({
          ...(currentClientId === selectedClientId
            ? { apps: sortedRestoredApps }
            : {}),
          deleteQueueProgress: {
            ...(this.app?.getAppState().deleteQueueProgress ?? {}),
            isQueueing: this.outstandingDeleteEnqueues > 0,
            isPolling: this.hasActiveDeleteQueue,
            errorMessage: message,
          },
          snackbar: {
            message,
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
      };

      let result: Awaited<ReturnType<typeof enqueueAppDeletion>>;
      try {
        result = await enqueueAppDeletion(reportId);
      } catch (error) {
        result = {
          success: false,
          error:
            typeof error === "object" && error !== null
              ? (error as Parameters<typeof localizeApiError>[1])
              : undefined,
        };
      }
      this.outstandingDeleteEnqueues -= 1;

      if (!result.success) {
        rollbackDeletion(result.error);
      } else {
        if (result.queueId && result.failureCount !== undefined) {
          const baseline = this.deleteQueueBaseline;
          if (!baseline) {
            this.deleteQueueBaseline = {
              queueId: result.queueId,
              failureCount: result.failureCount,
            };
          } else if (baseline.queueId !== result.queueId) {
            // Retention expiry or a backend restart: never claim all-success
            // when some of this run's outcomes are no longer available.
            this.deleteQueueHistoryLost = true;
          } else {
            // Concurrent enqueue responses may arrive in either order.
            baseline.failureCount = Math.min(
              baseline.failureCount,
              result.failureCount
            );
          }
        }
        this.hasActiveDeleteQueue = true;
        this.app?.patchAppState({
          deleteQueueProgress: {
            ...(this.app?.getAppState().deleteQueueProgress ?? {}),
            isQueueing: this.outstandingDeleteEnqueues > 0,
            isPolling: true,
            errorMessage: undefined,
          },
        });
      }
      if (this.hasActiveDeleteQueue && this.outstandingDeleteEnqueues === 0) {
        this.startDeleteQueuePolling(selectedClientId);
      }
    });

    homePortalComponent.on("logout", () => {
      window.location.href = `${BACKEND_URL}/keycloak/logout`;
    });

    homePortalComponent.on("navigate-admin", () => {
      this.app?.navigate(APP_ROUTES.admin);
    });

    homePortalComponent.on("invite-users", async ({ payload }) => {
      if (!payload) return;

      const { emails, role } = payload as {
        emails: string[];
        role: "Admin" | "User";
      };

      // Get client ID from the selected client (or fall back to first client)
      const state = this.app?.getAppState();
      const clientId = state?.selectedClient?.id ?? state?.clients?.[0]?.id;

      if (!clientId) {
        this.app?.patchAppState({
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.noClientFound"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
        return;
      }

      const result = await inviteUsers({
        emails,
        role,
        clientId,
        language: state?.language,
      });

      if (result.success && result.results) {
        // Refresh users list
        const usersResult = await fetchUsers(clientId);
        const { users, mfaPolicy } = usersResult;
        const fullySuccessful = result.results.filter(
          (r) => r.success && !r.error
        );
        const successCount = fullySuccessful.length;
        const totalCount = result.results.length;

        let message: string;
        let severity: "success" | "warning" | "error" = "success";

        if (successCount === totalCount) {
          message = getLocalizedMessage(
            state?.language || "de",
            "homePortalPage.inviteUsers.successAll"
          ).replace("{{count}}", String(successCount));
        } else if (successCount > 0) {
          // Some succeeded, some failed - show generic message
          message = getLocalizedMessage(
            state?.language || "de",
            "homePortalPage.inviteUsers.processCompleted"
          )
            .replace("{{successCount}}", String(successCount))
            .replace("{{totalCount}}", String(totalCount));
          severity = "warning";
        } else {
          // All failed
          message = getLocalizedMessage(
            state?.language || "de",
            "homePortalPage.inviteUsers.failedVerify"
          );
          severity = "error";
        }

        this.app?.patchAppState({
          users,
          mfaPolicy,
          snackbar: {
            message,
            severity,
            open: true,
            type: "snackbar",
          },
        });
      } else {
        // Generic error message - don't reveal details
        this.app?.patchAppState({
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.inviteUsers.failed"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
      }
    });

    homePortalComponent.on("delete-user", async ({ payload }) => {
      if (!payload) return;

      const { userId } = payload as { userId: string };
      const state = this.app?.getAppState();
      const selectedClientId =
        state?.selectedClient?.id ?? state?.clients?.[0]?.id;

      const result = await deleteUser(userId, selectedClientId);

      if (result.success) {
        // Refresh users list
        const usersResult = await fetchUsers(selectedClientId);
        const { users, mfaPolicy } = usersResult;
        this.app?.patchAppState({
          users,
          mfaPolicy,
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.deleteUser.success"
            ),
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      } else {
        this.app?.patchAppState({
          snackbar: {
            message: localizeApiError(
              state?.language || "de",
              result.error,
              "homePortalPage.deleteUser.failed"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
      }
    });

    homePortalComponent.on("change-role", async ({ payload }) => {
      if (!payload) return;

      const { userId } = payload as { userId: string };

      // Get current user to determine new role (toggle between Admin and User)
      const state = this.app?.getAppState();
      const users = state?.users ?? [];
      const user = users.find((u) => u.id === userId);

      if (!user) {
        this.app?.patchAppState({
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.userNotFound"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
        return;
      }

      // Toggle role: Admin -> User, User -> Admin
      const newRole =
        user.role === USER_ROLE.ADMIN ? USER_ROLE.USER : USER_ROLE.ADMIN;

      // If changing from User to Admin, show confirmation dialog
      if (user.role === USER_ROLE.USER && newRole === USER_ROLE.ADMIN) {
        this.app?.patchAppState({
          showRoleChangeConfirmDialog: true,
          pendingRoleChangeUserId: userId,
        });
        return;
      }

      // Otherwise, proceed with role change immediately
      const selectedClientId =
        state?.selectedClient?.id ?? state?.clients?.[0]?.id;
      const result = await changeUserRole(userId, newRole, selectedClientId);

      if (result.success) {
        // Refresh users list
        const updatedUsersResult = await fetchUsers(selectedClientId);
        const { users: updatedUsers, mfaPolicy } = updatedUsersResult;
        this.app?.patchAppState({
          users: updatedUsers,
          mfaPolicy,
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.changeRole.success"
            ).replace("{{role}}", String(newRole)),
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      } else {
        this.app?.patchAppState({
          snackbar: {
            message: localizeApiError(
              state?.language || "de",
              result.error,
              "homePortalPage.changeRole.failed"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
      }
    });

    homePortalComponent.on("confirm-role-change", async ({ payload }) => {
      if (!payload) return;

      const { userId } = payload as { userId: string };

      // Get current user to determine new role
      const state = this.app?.getAppState();
      const users = state?.users ?? [];
      const user = users.find((u) => u.id === userId);

      if (!user) {
        this.app?.patchAppState({
          showRoleChangeConfirmDialog: false,
          pendingRoleChangeUserId: undefined,
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.userNotFound"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
        return;
      }

      const selectedClientId =
        state?.selectedClient?.id ?? state?.clients?.[0]?.id;
      const result = await changeUserRole(
        userId,
        USER_ROLE.ADMIN,
        selectedClientId
      );

      this.app?.patchAppState({
        showRoleChangeConfirmDialog: false,
        pendingRoleChangeUserId: undefined,
      });

      if (result.success) {
        const updatedUsersResult = await fetchUsers(selectedClientId);
        const { users: updatedUsers, mfaPolicy } = updatedUsersResult;
        this.app?.patchAppState({
          users: updatedUsers,
          mfaPolicy,
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.changeRole.success"
            ).replace("{{role}}", String(USER_ROLE.ADMIN)),
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      } else {
        this.app?.patchAppState({
          snackbar: {
            message: localizeApiError(
              state?.language || "de",
              result.error,
              "homePortalPage.changeRole.failed"
            ),
            severity: "error",
            open: true,
            type: "snackbar",
          },
        });
      }
    });

    homePortalComponent.on("cancel-role-change", () => {
      this.app?.patchAppState({
        showRoleChangeConfirmDialog: false,
        pendingRoleChangeUserId: undefined,
      });
    });

    homePortalComponent.on("transfer-ownership", ({ payload }) => {
      if (!payload) return;
      const { userId } = payload as { userId: string };
      this.app?.patchAppState({
        showTransferOwnershipConfirmDialog: true,
        pendingTransferOwnershipUserId: userId,
      });
    });

    homePortalComponent.on(
      "confirm-transfer-ownership",
      async ({ payload }) => {
        if (!payload) return;

        const { userId } = payload as { userId: string };
        const state = this.app?.getAppState();
        const selectedClientId =
          state?.selectedClient?.id ?? state?.clients?.[0]?.id;

        const result = await transferOwnership(userId, selectedClientId);

        this.app?.patchAppState({
          showTransferOwnershipConfirmDialog: false,
          pendingTransferOwnershipUserId: undefined,
        });

        if (!result.success) {
          this.app?.patchAppState({
            snackbar: {
              message: localizeApiError(
                state?.language || "de",
                result.error,
                "homePortalPage.ownership.transferFailed"
              ),
              severity: "error",
              open: true,
              type: "snackbar",
            },
          });
          return;
        }

        const [clients, usersResult] = await Promise.all([
          fetchClients(),
          fetchUsers(selectedClientId),
        ]);
        const { users, mfaPolicy } = usersResult;
        const selectedClient =
          clients.find((c) => c.id === selectedClientId) ?? clients?.[0];
        // The actor just gave away ownership; reflect their new Admin role in
        // app state so owner-only UI hides immediately (the backend demotes
        // their session to Admin too).
        const profile = state?.userProfile;
        const userProfile = profile
          ? { ...profile, role: USER_ROLE.ADMIN }
          : undefined;

        this.app?.patchAppState({
          clients,
          selectedClient,
          users,
          mfaPolicy,
          userProfile,
          snackbar: {
            message: getLocalizedMessage(
              state?.language || "de",
              "homePortalPage.ownership.transferSuccess"
            ),
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      }
    );

    homePortalComponent.on("cancel-transfer-ownership", () => {
      this.app?.patchAppState({
        showTransferOwnershipConfirmDialog: false,
        pendingTransferOwnershipUserId: undefined,
      });
    });

    homePortalComponent.on("refresh-users", async () => {
      const state = this.app?.getAppState();
      const selectedClientId =
        state?.selectedClient?.id ?? state?.clients?.[0]?.id;
      const usersResult = await fetchUsers(selectedClientId);
      const { users, mfaPolicy } = usersResult;
      this.app?.patchAppState({
        users,
        mfaPolicy,
      });
    });

    createAppModalComponent.on("close-modal", () => {
      this.app?.patchAppState({
        isOpen: false,
        isCreatingApp: false,
        successMessage: undefined,
      });
    });

    createAppModalComponent.on("create-app", async ({ payload }) => {
      if (!payload) return;

      const appData = payload as CreateAppData;
      if (!appData.name) {
        return;
      }

      const currentAppState = this.app?.getAppState();
      const selectedClientId =
        currentAppState?.selectedClient?.id ??
        currentAppState?.clients?.[0]?.id;

      this.app?.patchAppState({
        isCreatingApp: true,
        successMessage: undefined,
      });

      const result = await createApp({
        ...appData,
        clientId: selectedClientId,
        // Defensive fallback: keep working even if a caller/older client
        // omits `language` from the create-app payload.
        language: appData.language ?? currentAppState?.language,
      });

      if (result.success && result.app) {
        const currentState = this.app?.getAppState();
        const selectedClientId =
          currentState?.selectedClient?.id ?? currentState?.clients?.[0]?.id;
        const apps = await fetchApps(selectedClientId);
        this.app?.patchAppState({
          apps,
          isCreatingApp: false,
          successMessage: getLocalizedMessage(
            currentAppState?.language || "de",
            "createAppModal.appCreatedSuccessfully"
          ),
        });
      } else {
        const language = currentAppState?.language || "de";
        const { message, referenceId } = getErrorToast(result, language);
        this.app?.patchAppState({
          isCreatingApp: false,
          successMessage: undefined,
          snackbar: {
            message,
            severity: "error",
            open: true,
            type: "snackbar",
            copy: referenceId
              ? {
                  text: referenceId,
                  label: getLocalizedMessage(language, "homePortalPage.copy"),
                  copiedLabel: getLocalizedMessage(
                    language,
                    "homePortalPage.copied"
                  ),
                }
              : undefined,
          },
        });
      }
    });

    const route = app.createRoute({
      path: APP_ROUTES.homePortal,
      view,
      isOverlayChild: true,
    });

    route.on("load", async () => {
      if (!this.app) return;

      const [profile, clients] = await Promise.all([
        fetchUserProfile(),
        fetchClients(),
      ]);

      // Default to the first client
      const selectedClient = clients?.[0];
      const selectedClientId = selectedClient?.id;
      const language = selectedClient?.language as SupportedLanguage;

      // Fetch apps, users, plans, subscription, seat info, billing and
      // invoice history scoped to client
      const [
        apps,
        usersResult,
        plans,
        subscription,
        seatInfo,
        billingAddress,
        invoices,
        deleteQueueStatus,
      ] = await Promise.all([
        fetchApps(selectedClientId),
        fetchUsers(selectedClientId),
        fetchPlans().catch(() => []),
        selectedClientId
          ? fetchSubscription(selectedClientId).catch(() => null)
          : Promise.resolve(null),
        selectedClientId
          ? fetchSeatInfo(selectedClientId).catch(() => null)
          : Promise.resolve(null),
        selectedClientId
          ? fetchBillingAddress(selectedClientId).catch(() => null)
          : Promise.resolve(null),
        selectedClientId
          ? fetchInvoices(selectedClientId).catch(() => [])
          : Promise.resolve([]),
        selectedClientId
          ? fetchAppDeleteQueueStatus(selectedClientId)
          : Promise.resolve(null),
      ]);

      const shouldResumeDeleteQueue =
        deleteQueueStatus && !isDeleteQueueTerminal(deleteQueueStatus);
      const visibleApps = filterAppsWithActiveDeleteEntries(
        apps,
        deleteQueueStatus,
        selectedClientId
      );

      const { users, mfaPolicy } = usersResult;

      const userProfile = profile
        ? {
            ...profile,
            role: getUserRoleFromProfile(profile, selectedClient?.name),
          }
        : undefined;

      this.app.patchAppState({
        userProfile,
        clients,
        selectedClient,
        apps: visibleApps,
        users,
        plans,
        subscription,
        seatInfo,
        billingAddress,
        invoices,
        mfaPolicy,
        ...(shouldResumeDeleteQueue
          ? {
              deleteQueueProgress: {
                isQueueing: false,
                isPolling: true,
                status: deleteQueueStatus,
              },
            }
          : {}),
        ...(language ? { language } : {}),
      });

      if (shouldResumeDeleteQueue) {
        this.hasActiveDeleteQueue = true;
        this.startDeleteQueuePolling(selectedClientId);
      }
    });
  }
}
